/**
 * Consent-preview protocol — the console's "Verification screen" settings frame
 * `/preview/consent` and push the branding an operator is typing into it, so the
 * preview IS the applicant's consent screen rather than a drawing of it.
 *
 * Messages are accepted only from the console origins this build was told
 * about (`FRONTEND_PREVIEW_PARENT_ORIGINS`). Without that, any site could frame the
 * preview on our domain and have it read "Bangkok Bank works with trustsy"; no
 * preview sends what is entered on it anywhere, so the allowlist decides whose
 * words appear on it, not whether data can be collected.
 */
import type { DocumentKind } from '../flow/documents';
import type { Screen } from '../flow/screen';
import type { TenantBranding, TenantStyle } from '../flow/types';

/** Console → preview: the branding to render, as the operator is editing it. */
export interface PreviewBrandingMessage {
  type: 'trustsy:preview-branding';
  branding: TenantBranding;
  /** UI language to render in, so the preview matches the console. */
  language?: string;
}

/**
 * Console → flow preview: one state of a draft workflow, as the agentic builder
 * has it right now. The fields are the ones the live flow's snapshot carries
 * for the same state, so the preview classifies it with the same `stepFor` —
 * the frame renders what an applicant would see on that step, not a drawing.
 */
export interface PreviewStepMessage {
  type: 'trustsy:preview-step';
  step: {
    /** Dotted state path, e.g. `data_collection.awaiting_consent`. */
    state: string;
    tags: string[];
    /** The events the state accepts — the live snapshot's `available_events`. */
    events: string[];
    /** A top-level `type: final` state. */
    final: boolean;
  };
  branding: TenantBranding;
  language?: string;
  /**
   * The case the operator is previewing: the applicant's issuing country, and
   * which document they will pick at a document step (null = show the choice).
   */
  scenario?: PreviewScenario;
}

export interface PreviewScenario {
  /** ISO 3166-1 alpha-2. */
  country: string;
  document: DocumentKind | null;
}

/**
 * Console → flow preview, from a case's session replay: ring the control a
 * recorded press landed on, or take the ring away (`xpath: null`). `at` is the
 * press's place on the replay's clock, so the same control pressed twice rings
 * twice.
 */
export interface PreviewHighlightMessage {
  type: 'trustsy:preview-highlight';
  xpath: string | null;
  at: number;
}

/** Preview → console. */
export type PreviewOutboundMessage =
  | { type: 'trustsy:preview-ready' }
  | { type: 'trustsy:preview-size'; height: number }
  /**
   * The language the frame now renders in — also when the applicant's own
   * EN/ไทย toggle switched it, so the console's language control can follow.
   */
  | { type: 'trustsy:preview-language'; language: string }
  /**
   * The applicant acted on a step of a draft flow: the event the act fires on
   * a live case, for the console to follow. Only its NAME crosses — what was
   * typed, picked or drawn stays in this frame.
   */
  | { type: 'trustsy:preview-event'; state: string; event: string }
  /**
   * The screen shown for a step, as `screenFor` picked it — so the console
   * knows an automated check without keeping its own list of applicant events.
   */
  | { type: 'trustsy:preview-screen'; state: string; screen: Screen['kind'] }
  /** Whether a replayed press could be placed on the screen on show. */
  | { type: 'trustsy:preview-pressed'; at: number; found: boolean };

const BRANDING_KEYS = [
  'display_name',
  'logo_url',
  'website_url',
  'privacy_url',
  'terms_url',
] as const satisfies readonly (keyof TenantBranding)[];

/**
 * Only our own brand-asset path is rendered as the logo. The console sends the
 * stored `logo_url`, which is always this shape; anything else — an absolute
 * URL in particular — would let a message choose an arbitrary image to show on
 * this origin.
 */
const BRAND_ASSET_PATH = /^\/api\/v1\/brand-assets\/[A-Za-z0-9_-]+$/;

function optionalString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : undefined;
}

const HEX_COLOR = /^#[\da-f]{6}(?:[\da-f]{2})?$/i;

/** Optional customization: anything malformed is dropped, never the message. */
function parseStyle(value: unknown): TenantStyle | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const colors: Record<string, string> = {};
  if (typeof raw.colors === 'object' && raw.colors !== null) {
    for (const [role, color] of Object.entries(raw.colors)) {
      if (typeof color === 'string' && HEX_COLOR.test(color)) colors[role] = color;
    }
  }
  return {
    colors,
    radius: typeof raw.radius === 'number' ? raw.radius : null,
    font: typeof raw.font === 'string' ? raw.font : null,
  };
}

function parseTexts(value: unknown): Record<string, Record<string, string>> | null {
  if (typeof value !== 'object' || value === null) return null;
  const out: Record<string, Record<string, string>> = {};
  for (const [lng, entries] of Object.entries(value as Record<string, unknown>)) {
    if (typeof entries !== 'object' || entries === null) continue;
    out[lng] = Object.fromEntries(
      Object.entries(entries as Record<string, unknown>).filter(
        (e): e is [string, string] => typeof e[1] === 'string',
      ),
    );
  }
  return out;
}

function parseBranding(value: unknown): TenantBranding | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const branding = {} as TenantBranding;
  for (const key of BRANDING_KEYS) {
    const field = optionalString(raw[key]);
    if (field === undefined) return null;
    branding[key] = field;
  }
  if (branding.logo_url !== null && !BRAND_ASSET_PATH.test(branding.logo_url)) {
    branding.logo_url = null;
  }
  // Customization keys are optional on the wire: a message without them parses
  // exactly as before; one with them gets them checked.
  if ('style' in raw) branding.style = parseStyle(raw.style);
  if ('texts' in raw) branding.texts = parseTexts(raw.texts);
  if ('favicon_url' in raw) {
    const favicon = optionalString(raw.favicon_url);
    branding.favicon_url = favicon && BRAND_ASSET_PATH.test(favicon) ? favicon : null;
  }
  return branding;
}

function languageOf(record: Record<string, unknown>): string | undefined {
  return typeof record.language === 'string' ? record.language : undefined;
}

/**
 * Validate an inbound message. Untrusted `postMessage` data is `unknown`;
 * anything that is not exactly a branding message is dropped.
 */
export function parseBrandingMessage(data: unknown): PreviewBrandingMessage | null {
  if (typeof data !== 'object' || data === null) return null;
  const record = data as Record<string, unknown>;
  if (record.type !== 'trustsy:preview-branding') return null;
  const branding = parseBranding(record.branding);
  if (!branding) return null;
  return { type: 'trustsy:preview-branding', branding, language: languageOf(record) };
}

/** More than any real state carries; bounds what a message can make us render. */
const MAX_STEP_STRINGS = 64;

function stringList(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length > MAX_STEP_STRINGS) return null;
  return value.every((entry) => typeof entry === 'string') ? (value as string[]) : null;
}

/** As {@link parseBrandingMessage}, for a draft step. */
export function parseStepMessage(data: unknown): PreviewStepMessage | null {
  if (typeof data !== 'object' || data === null) return null;
  const record = data as Record<string, unknown>;
  if (record.type !== 'trustsy:preview-step') return null;
  const branding = parseBranding(record.branding);
  if (!branding || typeof record.step !== 'object' || record.step === null) return null;

  const step = record.step as Record<string, unknown>;
  const tags = stringList(step.tags);
  const events = stringList(step.events);
  if (typeof step.state !== 'string' || !tags || !events || typeof step.final !== 'boolean') {
    return null;
  }
  return {
    type: 'trustsy:preview-step',
    step: { state: step.state, tags, events, final: step.final },
    branding,
    language: languageOf(record),
    scenario: scenarioOf(record.scenario),
  };
}

const DOCUMENT_KINDS: readonly string[] = ['thai_id', 'driving_license', 'passport'];

/** A scenario only when it is exactly one; anything else is no scenario. */
function scenarioOf(value: unknown): PreviewScenario | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const { country, document } = value as Record<string, unknown>;
  if (typeof country !== 'string' || !/^[A-Z]{2}$/.test(country)) return undefined;
  if (document !== null && !(typeof document === 'string' && DOCUMENT_KINDS.includes(document))) {
    return undefined;
  }
  return { country, document: document as DocumentKind | null };
}

/**
 * The shape `flow/interactions.ts::xpathOf` writes: tag names and sibling
 * positions, nothing else. Anything richer is not a recorded press.
 */
const RECORDED_XPATH = /^\/\/?[a-z][a-z0-9-]*(?:\[\d+\])?(?:\/[a-z][a-z0-9-]*(?:\[\d+\])?)*$/;
/** As the recorder and the server cap it. */
const MAX_XPATH = 512;

export function parseHighlightMessage(data: unknown): PreviewHighlightMessage | null {
  if (typeof data !== 'object' || data === null) return null;
  const record = data as Record<string, unknown>;
  if (record.type !== 'trustsy:preview-highlight' || typeof record.at !== 'number') return null;
  const { xpath } = record;
  if (xpath === null) return { type: 'trustsy:preview-highlight', xpath: null, at: record.at };
  if (typeof xpath !== 'string' || xpath.length > MAX_XPATH || !RECORDED_XPATH.test(xpath)) {
    return null;
  }
  return { type: 'trustsy:preview-highlight', xpath, at: record.at };
}

const XPATH_STEP = /^([a-z][a-z0-9-]*)(?:\[(\d+)\])?$/;

/**
 * Where a recorded press sits in THIS page, or null when the page has no such
 * place. The live flow and the preview wrap a step differently (the preview
 * adds its ribbon), but both draw it inside `main.screen` with the same
 * components — so the path is followed from its last `main` down.
 *
 * The recorder writes no position under a container whose children are the
 * applicant's choices (a country list, a date grid). There, more than one child
 * fits the step, and the walk stops at the container: the ring says "one of
 * these", never which one — the choice was not recorded, so it is not shown.
 */
export function locateRecorded(xpath: string, doc: Document): Element | null {
  const steps = xpath.replace(/^\/+/, '').split('/');
  const from = steps.map((step) => step.replace(/\[\d+\]$/, '')).lastIndexOf('main');
  const main = doc.querySelector('main.screen');
  if (from < 0 || !main) return null;
  let node: Element = main;
  for (const step of steps.slice(from + 1)) {
    const match = XPATH_STEP.exec(step);
    if (!match) return null;
    const [, tag, position] = match;
    const same = [...node.children].filter((child) => child.localName === tag);
    const next = position ? same[Number(position) - 1] : same.length === 1 ? same[0] : undefined;
    if (next) {
      node = next;
      continue;
    }
    // No position and several fit: a choice the recorder kept to itself.
    return !position && same.length > 1 ? node : null;
  }
  return node;
}

/**
 * The console origins allowed to drive the preview, from a comma-separated
 * list. Entries that are not a bare origin (`'*'`, a path, garbage) are dropped
 * rather than trusted.
 */
export function parseParentOrigins(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => {
      try {
        return entry !== '' && new URL(entry).origin === entry;
      } catch {
        return false;
      }
    });
}
